import React from "react";
import { Alert, Badge, Card, Col, Row, Spinner, Table } from "react-bootstrap";
import { useParams } from "react-router";
import { CheckCircle, MessageSquare, ThumbsDown, ThumbsUp, XCircle } from "react-feather";
import { apiErrorMessage, useGet } from "../../api";
import { PageHeader, PageToolbar, ToolbarButton } from "../../components/ui";
import {
  ISmartShoppingSession,
  ISmartShoppingStoredTurn,
  SMART_SHOPPING_API_LABELS,
  SmartShoppingStepKind,
  SmartShoppingVerdict,
} from "../../interfaces";
import { JsonBlock } from "../open-data/json-block";
import { SessionEvaluation, TurnEvaluation } from "./evaluation";
import { formatCost } from "./sessions";

const VERDICT_VARIANT: Record<SmartShoppingVerdict, string> = { pass: "success", warn: "warning", fail: "danger" };
const STATUS_VARIANT: Record<ISmartShoppingStoredTurn["status"], string> = { completed: "success", failed: "danger", cancelled: "secondary" };

const seconds = (ms: number) => (ms < 1000 ? `${ms}ms` : `${(ms / 1000).toFixed(1)}s`);

const Turn: React.FC<{ turn: ISmartShoppingSession["turns"][number]; index: number }> = ({ turn, index }) => {
  const u = turn.usage;
  return (
    <Card className="mb-3">
      <Card.Header className="d-flex flex-wrap align-items-center gap-2 py-2">
        <strong>Turn {index + 1}</strong>
        <Badge bg={STATUS_VARIANT[turn.status]}>{turn.status}</Badge>
        <Badge bg="light" text="dark">
          {turn.market}
        </Badge>
        {turn.domain && <span className="small text-muted">on {turn.domain}</span>}
        <span className="ms-auto small text-muted">
          {new Date(turn.startedAt).toLocaleString()} · {seconds(turn.durationMs)} · {formatCost(u?.costUsd)}
        </span>
      </Card.Header>
      <Card.Body>
        <div className="mb-2">
          <div className="small text-muted fw-semibold">Shopper</div>
          <div style={{ whiteSpace: "pre-wrap" }}>{turn.message}</div>
        </div>
        <div className="mb-3">
          <div className="small text-muted fw-semibold">Agent</div>
          <div style={{ whiteSpace: "pre-wrap" }}>{turn.answer || <span className="text-muted">(no text answer)</span>}</div>
          {turn.error && <Alert variant="danger" className="py-1 px-2 mt-2 small mb-0">{turn.error}</Alert>}
        </div>

        {turn.evaluation && <TurnEvaluation evaluation={turn.evaluation} />}

        <Row className="g-3 mb-3">
          <Col md={6}>
            <div className="small text-muted fw-semibold mb-1">Judge</div>
            {turn.judge ? (
              <>
                <div className="d-flex align-items-center gap-2 mb-1">
                  <Badge bg={VERDICT_VARIANT[turn.judge.verdict]}>{turn.judge.verdict}</Badge>
                  <span className="small">score {turn.judge.score}</span>
                  {turn.judge.revised && <Badge bg="info">answer revised</Badge>}
                  <span className="small text-muted">
                    {turn.judge.provider}
                    {turn.judge.model ? ` · ${turn.judge.model}` : ""}
                  </span>
                </div>
                <div className="small mb-1">{turn.judge.summary}</div>
                <ul className="list-unstyled small mb-0">
                  {turn.judge.checks.map((c) => (
                    <li key={c.id} className="d-flex gap-2">
                      {c.verdict === "pass" ? <CheckCircle size={13} className="text-success mt-1" /> : <XCircle size={13} className={`mt-1 text-${VERDICT_VARIANT[c.verdict]}`} />}
                      <span>
                        <strong>{c.label}</strong> — {c.note}
                      </span>
                    </li>
                  ))}
                </ul>
              </>
            ) : (
              <span className="small text-muted">Not judged</span>
            )}
          </Col>
          <Col md={6}>
            <div className="small text-muted fw-semibold mb-1">Shopper feedback</div>
            {turn.feedback ? (
              <div className="small">
                {turn.feedback.rating === 1 ? <ThumbsUp size={14} className="text-success" /> : <ThumbsDown size={14} className="text-danger" />}
                {turn.feedback.comment && (
                  <span className="ms-2">
                    <MessageSquare size={12} className="me-1" />
                    {turn.feedback.comment}
                  </span>
                )}
              </div>
            ) : (
              <span className="small text-muted">None</span>
            )}
            {u && (
              <div className="small text-muted mt-2">
                {u.requests} LLM calls · {u.inputTokens.toLocaleString()} in / {u.outputTokens.toLocaleString()} out · {u.kindredCalls} Kindred calls · {u.webSearches} searches ·{" "}
                {u.mapActions} map actions · {u.computerActions} computer actions · {u.screenshots} screenshots
              </div>
            )}
          </Col>
        </Row>

        <Table size="sm" responsive className="small mb-2 align-middle">
          <thead>
            <tr>
              <th>Step</th>
              <th>Tool</th>
              <th>Result</th>
              <th className="text-end">Time</th>
            </tr>
          </thead>
          <tbody>
            {turn.steps.map((s) => (
              <tr key={s.id}>
                <td>
                  {s.kind === SmartShoppingStepKind.Kindred && s.api && (
                    <Badge bg="primary" className="me-1">
                      {SMART_SHOPPING_API_LABELS[s.api].name}
                    </Badge>
                  )}
                  {s.label}
                </td>
                <td className="text-muted font-monospace">{s.tool}</td>
                <td>
                  <Badge bg={s.status === "ok" ? "success" : s.status === "rejected" ? "warning" : "danger"} className="me-1">
                    {s.status}
                  </Badge>
                  {s.summary}
                </td>
                <td className="text-end text-nowrap">{seconds(s.durationMs)}</td>
              </tr>
            ))}
          </tbody>
        </Table>
        {turn.blocks.length > 0 && <JsonBlock value={turn.blocks} title={`Cards shown (${turn.blocks.length})`} />}
        <JsonBlock value={{ settings: turn.settings, usage: turn.usage }} title="Settings and usage" />
      </Card.Body>
    </Card>
  );
};

export const SmartShoppingSessionDetail: React.FC = () => {
  const { conversationId } = useParams();
  const { data, loading, error } = useGet<ISmartShoppingSession>(conversationId ? `smart-shopping/sessions/${conversationId}` : null);

  return (
    <div>
      <PageHeader
        title="Smart Shopping session"
        description={data ? `${data.turnCount} turn${data.turnCount === 1 ? "" : "s"} · ${data.accessLabel ?? "anonymous"} · extension ${data.extensionVersion ?? "?"}` : undefined}
      />
      <PageToolbar>
        <ToolbarButton label="All sessions" icon="bi-arrow-left" to="/smart-shopping/sessions" hint="Back to the list of sessions" />
      </PageToolbar>
      {error && <Alert variant="danger">{apiErrorMessage(error)}</Alert>}
      {loading && (
        <div className="text-center py-5">
          <Spinner animation="border" size="sm" />
        </div>
      )}
      {data && (
        <>
          <div className="small text-muted mb-3">
            Conversation <code>{data.conversationId}</code> · started {new Date(data.createdAt).toLocaleString()} · {formatCost(data.totalCostUsd)} total · install{" "}
            <code>{data.installId.slice(0, 8)}</code>
          </div>
          <SessionEvaluation session={data} />
          {data.turns.map((t, i) => (
            <Turn key={t.turnId} turn={t} index={i} />
          ))}
        </>
      )}
    </div>
  );
};
